import {
  ContentUpdateMessage,
  CVEditingSection,
  CVSection,
  FullCV,
  getCVCurrentSection,
} from '@cibli/shared';
import { Micdrop } from '@micdrop/web';
import { useCallback, useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { useVocalCVEditContext } from '../contexts/vocal-cv-edit.context.tsx';

/**
 * Hook to sync form values with vocal updates
 * - Listens to vocal updates and applies them to the form
 * - Updates currentSectionContent when form values change manually
 */
export function useVocalFormUpdate<T extends Partial<FullCV>>(
  form: UseFormReturn<T>,
  sectionType: CVEditingSection,
) {
  const {
    setOnVocalUpdate,
    currentEditingSection,
    setCurrentSectionContent,
    setCurrentEditingSection,
    stopVocalEditing,
  } = useVocalCVEditContext();

  const cleanUp = useCallback(() => {
    setOnVocalUpdate(undefined);
    setCurrentSectionContent(null);
    setCurrentEditingSection(null);
    stopVocalEditing();
  }, [stopVocalEditing, setOnVocalUpdate, setCurrentSectionContent, setCurrentEditingSection]);

  const handleVocalUpdate = useCallback(
    (updatedSectionCv: CVSection) => {
      if (!updatedSectionCv) return;

      const sectionData: Partial<FullCV> = getCVCurrentSection(updatedSectionCv, sectionType);

      if (Object.keys(sectionData).length > 0) {
        // Merge over current values: the vocal schema only extracts a subset of
        // the section fields (e.g. no firstName/lastName/phone/email in
        // personalInfos) — a plain reset would wipe them from the form.
        form.reset({ ...form.getValues(), ...sectionData } as T);
      }
    },
    [form, sectionType],
  );

  useEffect(() => {
    setCurrentEditingSection(sectionType);
    setCurrentSectionContent(form.getValues());
    setOnVocalUpdate(() => handleVocalUpdate);

    return () => {
      cleanUp();
    };
  }, [
    setCurrentEditingSection,
    setCurrentSectionContent,
    form,
    sectionType,
    handleVocalUpdate,
    setOnVocalUpdate,
    cleanUp,
  ]);

  // Update currentSectionContent when form values change manually and send to backend
  useEffect(() => {
    // Only update if this is the currently editing section
    if (currentEditingSection !== sectionType) {
      return;
    }

    // Debounce to avoid sending too many messages
    let timeoutId: NodeJS.Timeout | undefined;

    const subscription = form.watch(values => {
      // Send content update to backend after a short delay (debounce)
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      timeoutId = setTimeout(() => {
        const sectionContent = values as Partial<FullCV>;

        if (sectionContent && Micdrop.isStarted) {
          try {
            const ws = (Micdrop as unknown as { ws?: WebSocket }).ws;
            if (ws?.readyState === WebSocket.OPEN) {
              const message: ContentUpdateMessage = {
                type: 'content_update',
                sectionContent,
              };
              ws.send(JSON.stringify(message));
            }
          } catch (error) {
            console.warn('Failed to send content update to backend:', error);
          }
        }
      }, 500);
    });

    return () => {
      subscription.unsubscribe();
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [form, sectionType, currentEditingSection]);
}
